import { computed, inject, Injectable, Signal, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { EVENT } from '@app/analytics/analytics.cnst'
import { AnalyticsService } from '@app/analytics/analytics.service'
import { B1_ERROR } from '@app/cnst/b1.cnst.js'
import { usesBluetoothDevice } from '@app/cnst/hardware.cnst'
import { ROUTES } from '@app/cnst/nav.cnst.js'
import { SNACKBAR } from '@app/cnst/snackbars.cnst'
import { isIOSApp, isWebApp } from '@app/cnst/userDevice.cnst'
import { tryCatch } from '@app/decorators/tryCatch.decorator'
import { B1_SERVICE_UUID } from '@app/model/b1.model'
import { HardwareDevice } from '@app/reducers/hardwareDevice.reducer'
import { dispatch, select } from '@app/srv/store.service'
import { distinctUntilDeeplyChanged } from '@app/util/distinctUntilDeeplyChanged'
import { logUtil } from '@app/util/log.util'
import { BleDevice, ScanMode } from '@capacitor-community/bluetooth-le'
import { NavController } from '@ionic/angular'
import { _Memo, AppError, localDate, pDefer, StringMap } from '@naturalcycles/js-lib'
import { FWVersionSeverity, HardwareId, TodayPhaseId } from '@naturalcycles/shared'
import { env } from '@src/environments/environment'
import { BluetoothClient, NativeSettings } from '@src/typings/capacitor'
import { BehaviorSubject, Subject, Subscription } from 'rxjs'
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  first,
  map,
  pairwise,
  startWith,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators'
import { B1Service } from './b1.service.js'
import { HardwareDeviceService } from './hardwareDevice.service.js'
import { LogService } from './log.service.js'
import { NavService } from './nav.service.js'
import { Priority } from './popup.cnst.js'
import { PopupController } from './popup.controller.js'
import { SnackbarService } from './snackbar.service.js'
import { getState } from './store.service.js'
import { T3_SERVICE_UUID } from './t3.cnst.js'
import { tr } from './translation.util.js'

export enum BluetoothStatus {
  UNPAIRED = 'unpaired',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  BLUETOOTH_OFF = 'bluetoothOff',
  SCANNING = 'scanning',
  FIRMWARE_UPDATE = 'firmware',
}

interface BluetoothConfig {
  hwId: HardwareId
  serviceUuid: string
  supportedDeviceNamePrefixes?: string[]
}

export const CONFIG_BY_HWID: StringMap<BluetoothConfig> = {
  [HardwareId.UEBE_THERMOMETER]: {
    hwId: HardwareId.UEBE_THERMOMETER,
    serviceUuid: '0000fff0-0000-1000-8000-00805f9b34fb',
    supportedDeviceNamePrefixes: [
      'mySense',
      'NC Thermometer1',
      'NC Thermometer2',
      'NC Thermometer (Gen2)',
    ],
  },
  [HardwareId.T3_THERMOMETER]: {
    hwId: HardwareId.T3_THERMOMETER,
    serviceUuid: T3_SERVICE_UUID.DEVICE,
    supportedDeviceNamePrefixes: ['TC'],
  },
  [HardwareId.B1]: {
    hwId: HardwareId.B1,
    serviceUuid: B1_SERVICE_UUID.DEVICE,
    supportedDeviceNamePrefixes: ['B1'],
  },
}

@Injectable({ providedIn: 'root' })
export class BluetoothService {
  private analyticsService = inject(AnalyticsService)
  private b1Service = inject(B1Service)
  private hardwareDeviceService = inject(HardwareDeviceService)
  private logService = inject(LogService)
  private navService = inject(NavService)
  private snackbarService = inject(SnackbarService)
  private router = inject(Router)
  private navController = inject(NavController)
  private popupController = inject(PopupController)

  public bluetoothEnabled$ = new BehaviorSubject<boolean | undefined>(undefined)
  public connected$ = new BehaviorSubject<BleDevice | false>(false)
  public bluetoothStatus$ = new BehaviorSubject<BluetoothStatus | undefined>(undefined)
  public firmwareUpdateOngoing$ = new BehaviorSubject<boolean>(false)
  public firmwareUpdateFailed$ = new Subject<boolean>()
  public inPairingFlow$ = new BehaviorSubject<boolean>(false)

  private hwDevice = toSignal(select(s => s.hwDevice))
  private latestFWVersion = toSignal(select(s => s.latestHWDeviceFWVersion))
  public availableFirmwareUpdate: Signal<FWVersionSeverity | false> = computed(() => {
    const fwVersion = this.hwDevice()?.fwVersion
    const latestFWVersion = this.latestFWVersion()

    if (!latestFWVersion || !fwVersion) return false
    if (fwVersion === latestFWVersion.version) return false

    return latestFWVersion.severity
  })

  private scanning$ = new BehaviorSubject<boolean>(false)
  private connecting$ = new BehaviorSubject<boolean>(false)
  private initializedFrom$ = new Subject<string>()

  private config = signal<BluetoothConfig | undefined>(undefined)

  private hwId$ = select(s => s.account.hwId)
  private hardwareDevice$ = select(s => s.hwDevice)
  private hardwareDeviceId$ = this.hardwareDevice$.pipe(map(s => s?.mac))
  private currentPhase$ = select(s => s.userFertility.currentPhase)
  private userFertilityFotaReminderShownDate = toSignal(
    select(s => s.userSettings.userFertilityFotaReminderShownDate),
  )

  private subscriptions: Subscription[] = []

  @_Memo()
  public init(): void {
    this.hwId$.pipe(combineLatestWith(this.inPairingFlow$)).subscribe(([hwId, inPairingFlow]) => {
      this.resetSubscriptions()

      if (!inPairingFlow && !usesBluetoothDevice(hwId)) return

      this.setupSubscriptions()
    })
  }

  private setupSubscriptions(): void {
    this.subscriptions.push(
      // bluetooth enabled notifications
      this.initializedFrom$.subscribe(async source => {
        void BluetoothClient.startEnabledNotifications(enabled => {
          this.logService.logBleAction(`Enabled changed: ${enabled}`)
          this.bluetoothEnabled$.next(enabled)
          this.analyticsService.trackEvent(EVENT.BLUETOOTH_ENABLED, { enabled })
        })

        const enabled = await this.isBluetoothEnabled()
        this.bluetoothEnabled$.next(enabled)
        this.analyticsService.trackEvent(EVENT.BLUETOOTH_ENABLED, { enabled, source })
      }),

      // stop scanning when hardware device is cleared
      this.hardwareDevice$
        .pipe(withLatestFrom(this.scanning$))
        .subscribe(([hwDevice, scanning]) => {
          if (hwDevice || !scanning) return

          void this.stopScan('HardwareDevice cleared')
        }),

      // bluetooth status
      this.hardwareDeviceId$
        .pipe(
          combineLatestWith(
            this.bluetoothEnabled$,
            this.scanning$,
            this.connecting$,
            this.connected$,
            this.firmwareUpdateOngoing$,
          ),
          distinctUntilDeeplyChanged(),
          map(([id, bluetoothEnabled, scanning, connecting, connected, firmwareUpdateOngoing]) => {
            if (bluetoothEnabled === false) return BluetoothStatus.BLUETOOTH_OFF // check explicitly for false - undefined means we dont know yet
            if (firmwareUpdateOngoing) return BluetoothStatus.FIRMWARE_UPDATE
            if (connected) return BluetoothStatus.CONNECTED
            if (connecting) return BluetoothStatus.CONNECTING
            if (scanning) return BluetoothStatus.SCANNING
            if (!id) return BluetoothStatus.UNPAIRED

            return BluetoothStatus.DISCONNECTED
          }),
          distinctUntilChanged(),
        )
        .subscribe(status => this.bluetoothStatus$.next(status)),

      // Show a prompt to perform FOTA when the user confirmed a period
      this.currentPhase$
        .pipe(
          startWith(undefined),
          pairwise(),
          filter(([previous, current]) => {
            // To prevent showing the prompt on the first load
            if (!previous?.todayPhaseId || !current?.todayPhaseId) return false
            // To prevent duplicate prompts
            if (previous.todayPhaseId === current.todayPhaseId) return false
            return current.todayPhaseId === TodayPhaseId.PERIOD_CONFIRMED
          }),
          map(async () => {
            if (!this.availableFirmwareUpdate()) return
            if (this.userFertilityFotaReminderShownDate() === localDate.todayString()) return

            const alert = await this.popupController.presentAlert(
              {
                header: tr('firmware-update-alert--title'),
                message: tr('firmware-update-alert--body'),
                buttons: [
                  {
                    text: tr('txt-remind-me-later'),
                    role: 'cancel',
                  },
                  {
                    text: tr('txt-update-now'),
                    role: 'confirm',
                  },
                ],
              },
              'alert-today-firmwareUpdate',
              Priority.HIGH,
              0,
            )

            const { role } = await alert.onWillDismiss()

            dispatch('extendUserSettings', {
              userFertilityFotaReminderShownDate: localDate.todayString(),
            })

            if (role !== 'confirm') return

            void this.navController.navigateForward(ROUTES.FirmwareUpdatePage)
          }),
        )
        .subscribe(),
    )
  }

  private resetSubscriptions(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions.length = 0 // this clears the array
    this.bluetoothStatus$.next(undefined)
  }

  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] initBluetooth ${error.message}`)
    },
  })
  public async initBluetooth(source: string): Promise<boolean> {
    if (isWebApp && !env.mockBluetooth) return false
    /*
     * Triggers enable bluetooth dialog and permission dialogs automatically (iOS only)
     * awaits permissions but not bluetooth settings
     */
    return await new Promise(resolve => {
      BluetoothClient.initialize({ androidNeverForLocation: true })
        .then(async () => {
          this.logService.logBleAction(`Bluetooth initialized`)
          logUtil.log('[BLE] init; should be all good')

          this.initializedFrom$.next(source)

          resolve(true)
        })
        .catch(err => {
          logUtil.log('[BLE] ERROR init', err)

          this.bluetoothEnabled$.next(false)

          resolve(false)
        })
    })
  }

  private async isBluetoothEnabled(): Promise<boolean> {
    if (isWebApp && !env.mockBluetooth) return false
    return await BluetoothClient.isEnabled()
  }

  public async enableBluetooth(showSnackbar = true): Promise<boolean> {
    if (isWebApp) {
      if (env.mockBluetooth) return true

      this.snackbarService.showSnackbar(SNACKBAR.UEBE_BLUETOOTH_OFF)

      return false
    }
    if (isIOSApp) {
      if (showSnackbar) this.snackbarService.showSnackbar(SNACKBAR.UEBE_BLUETOOTH_OFF)

      return false
    }

    // In some cases we need to check for permissions again, when it doesn't work we prompt the user to enable BT themselves
    const initialized = await this.initBluetooth('EnableBluetooth')
    if (!initialized) {
      if (showSnackbar) this.snackbarService.showSnackbar(SNACKBAR.UEBE_BLUETOOTH_OFF)

      return false
    }

    await NativeSettings.enableBluetooth()

    return await new Promise(resolve => {
      this.bluetoothEnabled$.pipe(first(Boolean)).subscribe(enabled => resolve(enabled))
    })
  }

  public async ensureBluetoothEnabled(showSnackbar = false): Promise<boolean> {
    let enabled = await this.isBluetoothEnabled()
    if (enabled) return true

    enabled = await this.enableBluetooth(showSnackbar)
    if (enabled) return true

    // Listen to bluetooth enabled changes if dialog dismissed
    return await new Promise(resolve => {
      this.bluetoothEnabled$.pipe(first(Boolean)).subscribe(enabled => resolve(enabled))
    })
  }

  public async ensureBluetoothScanning(): Promise<boolean> {
    const status = this.bluetoothStatus$.value

    if (status === BluetoothStatus.SCANNING) return true

    if (status === BluetoothStatus.BLUETOOTH_OFF) {
      await this.ensureBluetoothEnabled(true)
    }

    // Listen to bluetooth status changes until it starts searching
    return await new Promise(resolve => {
      this.bluetoothStatus$
        .pipe(takeWhile(status => status !== BluetoothStatus.SCANNING, true))
        .subscribe(status => resolve(status === BluetoothStatus.SCANNING))
    })
  }

  public async scanAndPair(hwId: HardwareId): Promise<BleDevice | false> {
    const config = this.setConfig(hwId)
    if (!config) return false

    this.analyticsService.trackEvent(EVENT.BLUETOOTH_SCANNING, { source: 'Pairing' })

    const device = await this.scanForDevice(config, undefined)
    if (!device) return await this.scanAndPair(hwId)
    return device
  }

  /**
   * @param pairedDeviceId is meant to be passed only for recurring calls. Avoid passing it for the first time.
   */
  public async scanAndConnect(pairedDeviceId: string): Promise<void> {
    if (!pairedDeviceId) return this.logService.logBleAction('Trying to scan with missing deviceId')

    const initialized = await this.initBluetooth('scanAndConnect')
    if (!initialized) return

    // TODO: should we address `false` somehow?
    await this.ensureBluetoothEnabled()

    const hwDevice = getState().hwDevice

    this.analyticsService.trackEvent(EVENT.BLUETOOTH_SCANNING, {
      source: 'Connecting',
      deviceId: pairedDeviceId,
      device: JSON.stringify(hwDevice),
    })

    if (!hwDevice) return

    const config = this.setConfig(undefined)
    if (!config) return

    await this.scanForDevice(config, hwDevice)
    return
  }

  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] scanForDevice ${error.message}`)
    },
  })
  private async scanForDevice(
    config: BluetoothConfig,
    hwDevice: HardwareDevice | undefined,
  ): Promise<BleDevice | false> {
    this.scanning$.next(true)

    this.logService.logBleAction(`Start scanning`)

    const foundDevice = pDefer<BleDevice>()
    void this.startScanning(config, hwDevice, async device => {
      const validDevice = await this.validateBleDevice(device, config, hwDevice)
      if (!validDevice) return false
      foundDevice.resolve(device)
      return true
    })

    const device = await foundDevice
    this.analyticsService.trackEvent(EVENT.BLUETOOTH_DEVICE_FOUND, { ...device })

    return await this.connectDevice(device, config)
  }

  private async startScanning(
    config: BluetoothConfig,
    hwDevice: HardwareDevice | undefined,
    callback: (result: BleDevice) => Promise<boolean>,
  ): Promise<void> {
    if (config.hwId === HardwareId.B1) {
      return await this.b1Service.startScanning(async device => await callback(device))
    }

    const scanningOptions = {
      scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
      services: [config.serviceUuid],
      allowDuplicates: true,
    }

    return await BluetoothClient.requestLEScan(
      scanningOptions,
      async ({ device }) => await callback(device),
    ).catch(async err => {
      logUtil.log('[BLE] ERROR startScanning', err.message)

      if (err.message.startsWith('Already scanning')) {
        await this.startScanning(config, hwDevice, callback)
      }
    })
  }

  private async validateBleDevice(
    device: BleDevice,
    config: BluetoothConfig,
    hwDevice: HardwareDevice | undefined,
  ): Promise<boolean> {
    this.analyticsService.trackEvent(EVENT.BLUETOOTH_SCANNING_RESULT, { ...device })

    this.logService.logBleAction(JSON.stringify(device))

    const { name, deviceId } = device
    // If result device has no name, then we want to continue scanning
    if (!name) return false

    // If we don't have a name prefix filter and paired device then we can return the device immediately
    const { hwId, supportedDeviceNamePrefixes } = config
    if (!supportedDeviceNamePrefixes?.length && !hwDevice) return true

    // Otherwise first check name prefix
    if (
      supportedDeviceNamePrefixes?.length &&
      !supportedDeviceNamePrefixes.some(prefix => name.startsWith(prefix))
    ) {
      return false
    }

    if (!hwDevice) return true

    // Save name if we don't have it yet
    if (!hwDevice.name && hwDevice.mac && hwDevice.mac === deviceId) {
      await this.hardwareDeviceService.extendHardwareDevice({ name })
    }

    // Filter out wrong devices of the same type if we look for a specific one
    if (hwId === HardwareId.UEBE_THERMOMETER) {
      if (hwDevice.mac && hwDevice.mac === deviceId) {
        return true
      }

      this.logService.logBleAction(
        `Found a T2 device: ${deviceId} while searching for: ${hwDevice.mac}`,
      )
      this.analyticsService.trackEvent(EVENT.BLUETOOTH_DEVICE_FOUND, { ...device })
      return false
    }

    if (hwId === HardwareId.T3_THERMOMETER) {
      if (hwDevice.name && hwDevice.name === name) {
        return true
      }

      this.logService.logBleAction(
        `Found a T3 device: ${deviceId} with name: ${name} while searching for: ${hwDevice.name}`,
      )
      this.analyticsService.trackEvent(EVENT.BLUETOOTH_DEVICE_FOUND, { ...device })
      return false
    }

    if (hwId === HardwareId.B1) {
      if (hwDevice.name && hwDevice.name === name) {
        return true
      }

      this.logService.logBleAction(
        `Found a B1 device: ${deviceId} with name: ${name} while searching for: ${hwDevice.name}`,
      )
      this.analyticsService.trackEvent(EVENT.BLUETOOTH_DEVICE_FOUND, { ...device })
      return false
    }

    return false
  }

  /**
   * iOS will throw if called without startScanning
   */
  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] connectDevice ${error.message}`)
    },
  })
  private async connectDevice(
    device: BleDevice,
    config: BluetoothConfig,
  ): Promise<BleDevice | false> {
    this.connecting$.next(true)

    await this.stopScan('Thermometer was found')

    const connected = await this.attemptToConnect(device, config).catch(err => {
      this.analyticsService.trackEvent(EVENT.BLUETOOTH_CONNECTION_ERROR, { error: err?.message })
      logUtil.error(`[BLE] attemptToConnect ${err?.message}`)
      return false
    })

    // if we are sure we are not connected due to timeout
    if (connected === false) {
      this.connected$.next(false)
      this.connecting$.next(false)
      return false
    }

    const connectedDevice = await this.verifyConnectedDevice(device, config)
    this.connected$.next(connectedDevice ?? false)
    this.connecting$.next(false)

    if (!connectedDevice) return false

    this.logService.logBleAction(`Connected to: ${JSON.stringify(connectedDevice)}`)
    const page = this.navService.getCurrentComponent()

    this.analyticsService.trackEvent(EVENT.BLUETOOTH_CONNECT, { page, route: this.router.url })

    return connectedDevice
  }

  private async attemptToConnect(device: BleDevice, config: BluetoothConfig): Promise<void> {
    this.analyticsService.trackEvent(EVENT.BLUETOOTH_CONNECTION_ATTEMPT, { ...device })

    if (config.hwId === HardwareId.B1) {
      if (!device.name) throw new Error('Device name is missing for B1')

      try {
        return await this.b1Service.connect(device.name)
      } catch (err) {
        if (!(err instanceof AppError)) throw err
        if (err.cause?.data.code !== B1_ERROR.WRONG_DEVICE_CONNECTED) throw err

        logUtil.warn((err as Error).message)
        await this.disconnect(device.name)
        // Will not try ... catch here because second throw will mean something is definitely wrong
        return await this.b1Service.connect(device.name)
      }
    }

    return await BluetoothClient.connect(device.deviceId, () => this.onDisconnect(), {
      timeout: 15_000,
    })
  }

  private onDisconnect(): void {
    this.connected$.next(false)
    this.config.set(undefined)

    this.analyticsService.trackEvent(EVENT.BLUETOOTH_DISCONNECT)

    this.logService.logBleAction('Disconnected')
  }

  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] stopScan ${error.message}`)
    },
  })
  public async stopScan(reason: string): Promise<void> {
    console.warn('[dev]', '[ble] stopScan')
    const config = this.config()
    if (!config) return

    // not currently scanning
    if (!this.scanning$.value) return

    this.logService.logBleAction(`Stop scanning because: ${reason}`)

    this.analyticsService.trackEvent(EVENT.BLUETOOTH_SCANNING_STOPPED, { reason })

    if (config.hwId === HardwareId.B1) {
      await this.b1Service.stopScanning()
    } else {
      await BluetoothClient.stopLEScan()
    }

    this.scanning$.next(false)
  }

  private async verifyConnectedDevice(
    expectedDevice: BleDevice,
    config: BluetoothConfig,
  ): Promise<BleDevice | undefined> {
    if (config.hwId === HardwareId.B1) {
      return await this.b1Service.verifyConnectedDevice(expectedDevice)
    }

    const connectedDevices = await BluetoothClient.getConnectedDevices([config.serviceUuid])
    return connectedDevices.find(device => device.deviceId === expectedDevice.deviceId)
  }

  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] disconnect ${error.message}`)
    },
  })
  public async disconnect(deviceId: string): Promise<void> {
    console.warn('[dev]', '[ble] disconnect', deviceId, this.connected$.value)
    if (!this.connected$.value) return

    const config = this.config()
    console.warn('[dev]', '[ble] disconnect config', config)
    if (!config) return

    if (config.hwId !== HardwareId.B1) {
      return await BluetoothClient.disconnect(deviceId)
    }

    await this.b1Service.disconnect()
    this.onDisconnect()
  }

  /**
   * @param hwId to be specified when doesn't match Account.hwId
   */
  private setConfig(hwId: HardwareId | undefined): BluetoothConfig | undefined {
    hwId ||= getState().account.hwId

    const config = CONFIG_BY_HWID[hwId]
    if (!config) {
      logUtil.error(`Trying to get BluetoothConfig for hwId ${hwId}`)

      this.config.set(undefined)
      return undefined
    }

    this.config.set(config)
    return config
  }
}
